export const SOURCE_ADAPTER_TYPES = Object.freeze({
  CSV: 'csv',
  JIRA_REST: 'jira-rest',
  SERVICENOW_REST: 'servicenow-rest',
});

export const SOURCE_ADAPTER_OPERATIONS = Object.freeze([
  'testConnection',
  'getSourceMetadata',
  'getAvailableProcesses',
  'getAvailableFields',
  'preview',
  'extract',
  'refresh',
]);

export function createSourceAdapterDefinition({
  type,
  label,
  description = '',
  requiresConnection = false,
  connectionTypes = [],
  capabilities = {},
} = {}) {
  if (!type) throw new Error('Source adaptor type is required');
  return Object.freeze({
    type,
    label: label || type,
    description,
    requiresConnection: Boolean(requiresConnection),
    connectionTypes: [...connectionTypes],
    capabilities: {
      testConnection: Boolean(capabilities.testConnection),
      getSourceMetadata: Boolean(capabilities.getSourceMetadata),
      getAvailableProcesses: Boolean(capabilities.getAvailableProcesses),
      getAvailableFields: Boolean(capabilities.getAvailableFields),
      preview: Boolean(capabilities.preview),
      extract: Boolean(capabilities.extract),
      refresh: Boolean(capabilities.refresh),
    },
  });
}

export const SOURCE_ADAPTER_CATALOG = Object.freeze({
  [SOURCE_ADAPTER_TYPES.CSV]: createSourceAdapterDefinition({
    type: SOURCE_ADAPTER_TYPES.CSV,
    label: 'CSV Upload',
    description: 'Upload an event log or case-record CSV and map it into the canonical process model.',
    requiresConnection: false,
    capabilities: {
      getSourceMetadata: true,
      getAvailableFields: true,
      preview: true,
      extract: true,
      refresh: true,
    },
  }),
  [SOURCE_ADAPTER_TYPES.JIRA_REST]: createSourceAdapterDefinition({
    type: SOURCE_ADAPTER_TYPES.JIRA_REST,
    label: 'Jira REST',
    description: 'Connect to Jira without installing the Forge app.',
    requiresConnection: true,
    connectionTypes: ['jira-rest'],
    capabilities: {
      testConnection: true,
      getSourceMetadata: true,
      getAvailableProcesses: true,
      getAvailableFields: true,
      preview: true,
      extract: true,
      refresh: true,
    },
  }),
  [SOURCE_ADAPTER_TYPES.SERVICENOW_REST]: createSourceAdapterDefinition({
    type: SOURCE_ADAPTER_TYPES.SERVICENOW_REST,
    label: 'ServiceNow REST',
    description: 'Connect to ServiceNow using a secure server-side connection.',
    requiresConnection: true,
    connectionTypes: ['servicenow-rest'],
    capabilities: {
      testConnection: true,
      getSourceMetadata: true,
      getAvailableProcesses: true,
      getAvailableFields: true,
      preview: true,
      extract: true,
      refresh: true,
    },
  }),
});

export function getSourceAdapterDefinition(type) {
  return SOURCE_ADAPTER_CATALOG[type] || null;
}

export function validateSourceAdapter(adapter) {
  const missing = SOURCE_ADAPTER_OPERATIONS.filter(name => typeof adapter?.[name] !== 'function');
  return { valid: missing.length === 0, missing };
}

export function defineSourceAdapter(adapter = {}) {
  const validation = validateSourceAdapter(adapter);
  if (!validation.valid) {
    throw new Error(`Source adaptor is missing operations: ${validation.missing.join(', ')}`);
  }
  return Object.freeze(adapter);
}
